import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useNavigate, createSearchParams } from 'react-router-dom';
import {
  ModalOverlay,
  ModalWrapper,
  ModalContent,
  ModalImage,
  ModalTitle,
  ModalDescription,
  ModalDescriptionContainer,
  StartButtonText,
  ModalAccessHeaderTitle,
  VerticalButtonsContainer,
  VerticalButtonsContainer2,
  ModalFooter,
  StartersContainer,
  AgentActionsContainer,
  StartChatContainer,
  KnowledgeBaseContainer,
} from './ModalAgentsDetail.styled';
import { RoundedButton, Icon, IconButton } from "../../../../../elements";
import GenericList from "../../../../../elements/v2/GenericList/GenericList";
import ActionsList from "../../../../../elements/v2/ActionsCatalog/ActionsList";
import KnowledgeBaseTable from "../../../../../elements/v2/KnowledgeBaseTable/KnowledgeBaseTable";
import VerticalButton from "../../../../../elements/verticalButton/VerticalButton";
import { useStore } from "../../../../../hooks";
import { CloseButtonWrapper } from "../../../../../elements/v2/modals/Modals.styled";
import { ConfirmationModal } from "../../../../../elements/v2/modals";
import SuccessModal from "../../../../../elements/v2/SuccessModal/SuccessModal";
import {PermissionCodes, PermissionLevels} from "../../../../../utils/enums";
import { useUserCheckPermissions } from "../../../../../hooks/useUserCheckPermissions";

const actionConfigs = {
  duplicate: {
    title: "Duplicate",
    message: "Caution: You are about to duplicate the agent. Are you sure you want to continue?",
    icon: "duplicate",
  },
  share: {
    title: "Share",
    message: "You are about to share this agent. Are you sure you want to continue?",
    icon: "Archive",
  },
  archive: {
    title: "Archive",
    message: "Caution: You are about to archive the agent. Are you sure you want to continue?",
    icon: "deleteOutline",
  },
};

const ModalAgentsDetail = observer(({ open, onClose, width, showGearIcon = false }) => {
  const navigate = useNavigate();
  const { agentStore } = useStore();
  const { selectedAgent } = agentStore
  const [, setAgentActions] = useState(selectedAgent?.actions || []);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showDuplicatingModal, setShowDuplicatingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [successTitle, setSuccessTitle] = useState("");
  const { checkPermission } = useUserCheckPermissions();

  useEffect(() => {
    if (agentStore.account) {
      agentStore.onChangeAccount(agentStore.account);
    }
  }, [agentStore.account, agentStore]);

  useEffect(() => {
    if (!agentStore.isDuplicating && showDuplicatingModal) {
      setShowDuplicatingModal(false);
      setSuccessMessage("Agent duplicated successfully.");
      setShowSuccessModal(true);
      setSuccessTitle("Success");
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentStore.isDuplicating, showDuplicatingModal]);

  const handleSaveAction = (newActions) => {
    setAgentActions(newActions);
  };

  const handleStartChat = () => {
    navigate({
      pathname: "/home",
      search: createSearchParams({
        agent: selectedAgent.id,
        customer: agentStore.account,
      }).toString(),
    });
  };

  const handleConfirmAction = () => {
    if(confirmAction === 'duplicate'){
      setShowConfirmModal(false);
      setShowDuplicatingModal(true);
      agentStore.duplicateAgent();
    }
    setShowConfirmModal(false);
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const handleVerticalButtonClick = (action) => {
    setConfirmAction(action);
    setShowConfirmModal(true);
  };

  if (!open || !selectedAgent) return null;

  return (
    <ModalOverlay>
      <ModalWrapper width={width}>
        <CloseButtonWrapper>
          <IconButton icon="close" width="16px" height="16px" onClick={onClose} dataTestId="close-modal-button" />
        </CloseButtonWrapper>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          {(() => {
            const hasDuplicatePermission = checkPermission(PermissionCodes.AGENTS_DUPLICATE, PermissionLevels.CREATE);
            const hasPlaygroundPermission = checkPermission(PermissionCodes.PLAYGROUND_VIEW, PermissionLevels.UPDATE);
            return (hasDuplicatePermission || hasPlaygroundPermission) && (
              <VerticalButtonsContainer id="vertical-button-container">
                <VerticalButton dataTestId={"vertical-button-menu-modal"} options={[
                  ...(hasDuplicatePermission ? [{
                    title: 'Duplicate',
                    onClick: () => handleVerticalButtonClick('duplicate')
                  }] : []),
                  ...(hasPlaygroundPermission ? [{
                    title: 'Playground',
                    onClick: () => {
                      onClose && onClose();
                      if (selectedAgent.typeAgent === "langchain" && !["", "empty"].includes(selectedAgent.prompt_id)) {
                        navigate(`/v2/prompt-management-system/${selectedAgent.prompt_id.split("#")[1]}`);
                      } else {
                        navigate({
                          pathname: "/v2/assistants/playground",
                          search: createSearchParams({ agent: selectedAgent.id }).toString(),
                        });
                      }
                    }
                  }] : [])
                ]} />
              </VerticalButtonsContainer>
            );
          })()}
          {showGearIcon && (
            <VerticalButtonsContainer2 id="vertical-button-container2">
              <Icon name="gearBlue" width="20px" height="20px" />
            </VerticalButtonsContainer2>
          )}

          <ModalImage src={selectedAgent.image} alt="Modal Image" sx={{ width: 100, height: 100 }}/>
          <ModalTitle>{selectedAgent.name}</ModalTitle>
          <ModalDescriptionContainer>
            <ModalDescription>{selectedAgent.description1}</ModalDescription>
          </ModalDescriptionContainer>
          {selectedAgent.conversation_starters && selectedAgent.conversation_starters.length > 0 && (
            <StartersContainer>
              <GenericList title="Starters" listItems={selectedAgent.conversation_starters} />
            </StartersContainer>
          )}
          {selectedAgent.actions && selectedAgent.actions.length > 0 && (
            <AgentActionsContainer>
              <ActionsList agentActions={selectedAgent.actions} onSave={handleSaveAction} />
            </AgentActionsContainer>
          )}
          <ModalAccessHeaderTitle>Knowledge base</ModalAccessHeaderTitle>
          <KnowledgeBaseContainer>
            <KnowledgeBaseTable dataTestId="knowledge-base-header" fileNames={selectedAgent.knowledgeBase} showAddButton={false} vaultName={selectedAgent.original?.customer_id}/>
          </KnowledgeBaseContainer>
        </ModalContent>
        <ModalFooter>
          <StartChatContainer>
            <RoundedButton onClick={handleStartChat} icon={{ position: 'left', element: <Icon name="conversation" /> }} width={'500px'} height={'48px'} dataTestId="start-chat-button"
            >
              <StartButtonText>Start chat</StartButtonText>
            </RoundedButton>
          </StartChatContainer>
        </ModalFooter>
        <ConfirmationModal
          isOpen={showConfirmModal}
          title={actionConfigs[confirmAction]?.title}
          message={actionConfigs[confirmAction]?.message}
          icon={actionConfigs[confirmAction]?.icon}
          onClose={handleCloseConfirmModal}
          onConfirm={handleConfirmAction}
        />
        <ConfirmationModal
          isOpen={showDuplicatingModal}
          title="Duplicating agent"
          message="This may take a few minutes. Please check back later to continue."
          icon="info"
          displayCancel={false}
          onConfirm={() => setShowDuplicatingModal(false)}
          onClose={() => setShowDuplicatingModal(false)}
        />
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          title={successTitle || "Success"}
          message={successMessage}
        />
      </ModalWrapper>
    </ModalOverlay>
  );
});

export default ModalAgentsDetail;
